<template>
  <div class="m-profile-page">
    <div class="sticky-header">
      <button class="back-btn" @click="$router.back()">
        ← Назад
      </button>
    </div>
    

    <main class="m-profile-content">
      <div v-if="isLoadingProfile" class="profile-skeleton">
        <div class="skeleton cover-skeleton-block"></div>
        <div class="skeleton avatar-skeleton"></div>
        <div class="skeleton name-skeleton"></div>
        <div class="skeleton field-skeleton"></div>
        <div class="skeleton field-skeleton"></div>
        <div class="skeleton field-skeleton large"></div>
      </div>

      
      <div v-else class="m-profile-general">
        <div class="cover" :style="{ backgroundImage: coverBg }">
          <div v-if="!coverLoaded" class="cover-skeleton"></div>

          <button
            type="button"
            class="edit-cover-btn"
            @click="coverInputRef?.click()"
            :disabled="isUploadingCover"
          >
            <img src="../../assets/edit-icon.png" class="edit-icon" />
          </button>

          <div v-if="isUploadingCover" class="cover-loader-overlay">
            <div class="cover-loader-spinner"></div>
          </div>

          <input
            type="file"
            ref="coverInputRef"
            accept="image/*"
            @change="onCoverChange"
            hidden
          />
        </div>

        <div class="avatar-wrapper">
          <img
            class="avatar"
            :src="user.image_url || avatarPlaceholder"
            @click="avatarInputRef?.click()"
          />

          <div v-if="isAvatarLoading" class="loader-overlay">
            <div class="loader-spinner"></div>
          </div>

          <input
            type="file"
            ref="avatarInputRef"
            accept="image/*"
            @change="onAvatarChange"
            hidden
          />
        </div>

        <div class="content">
          <div class="name-block">
            <div class="user-name">{{ user.name || 'Мой профиль' }}</div>
          </div>

          <div class="company-location" @click="openRegionDrawer">
            находится в
            <span class="location-tag">{{ locationText }}</span>
          </div>

          <div class="input-section">
            <label>
              Имя Фамилия
              <input type="text" v-model="form.name" />
            </label>

            <label>
              Специфика
              <input type="text" v-model="form.specifics" />
            </label>

            <label>
              Номер телефона
              <input
                type="tel"
                ref="phoneInputRef"
                v-model="form.phone_number"
                placeholder="+7 (999) ***-**-**"
              />
            </label>

            <label>
              Email
              <input
                type="email"
                v-model="form.email"
                placeholder="Введите ваш e-mail"
              />
            </label>

            <label>
              О себе
              <textarea
                v-model="form.about_self"
                placeholder="Ваша специфика, профессиональная и личная биография"
              />
            </label>
          </div>

          <label class="salary-label">Минимальная зарплата</label>

          <div class="salary-wrapper">
            <input
              type="text"
              v-model="form.salary"
              @input="form.salary = form.salary.replace(/\D/g, '')"
              placeholder="Например, 80000"
              class="salary-input"
            />

            <div class="currency-wrapper" @click.stop="toggleCurrencyMenu">
              <span class="salary-currency">{{ form.salary_currency }}</span>

              <transition name="slide-fade">
                <ul v-if="showCurrencyMenu" class="currency-dropdown">
                  <li
                    v-for="currency in currencies"
                    :key="currency"
                    @click.stop="changeCurrency(currency)"
                  >
                    {{ currency }}
                  </li>
                </ul>
              </transition>
            </div>
          </div>

          <div class="switch-section">
            <label class="switch-item">
              <span>Скрыть номер телефона?</span>
              <input type="checkbox" v-model="form.is_phone_number_hidden" />
              <span class="slider"></span>
            </label>

            <label class="switch-item">
              <span>Ищу только удалёнку</span>
              <input type="checkbox" v-model="form.is_remote" />
              <span class="slider"></span>
            </label>
          </div>
        </div>
      </div>
    </main>

    <transition name="fade">
      <button
        v-if="hasChanges || isSaving"
        class="floating-save-btn"
        :class="{ 'is-saving': isSaving }"
        :disabled="isSaving"
        @click="onSave"
      >
        {{ isSaving ? 'Сохраняем…' : 'Сохранить' }}
      </button>
    </transition>

    <transition name="fade">
      <div v-if="showSnackbar" class="snackbar">{{ snackbarMessage }}</div>
    </transition>

    <ErrorToast v-if="showError" :message="errorMessage" />

    <ProfileRegionDrawer
      v-if="showRegionDrawer"
      :visible="showRegionDrawer"
      :regions="regionList"
      :modelValue="selectedRegion"
      @update:modelValue="selectedRegion = $event"
      @close="showRegionDrawer = false"
    />

    <ProfileHashtagsDrawer
      :visible="showHashtagDrawer"
      :initialHashtags="user.hashtags || []"
      @close="showHashtagDrawer = false"
    />

    <MobileTabBar />
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import FeedHeader from '../FeedHeader.vue';
import MobileTabBar from '../MobileTabBar2.vue';
import UserService from '../../api/UserService';
import ErrorToast from '../../components/ErrorToast.vue';
import ProfileHashtagsDrawer from '../profile/ProfileHashtagsDrawer.vue';
import ProfileRegionDrawer from '../profile/ProfileRegionDrawer.vue';
import IMask from 'imask';
import avatarPlaceholder from '../../assets/user.png';

const gradients = [
  'linear-gradient(135deg, #7B61FF, #C972E6)',
  'linear-gradient(135deg, #2173E5, #204F90)',
  'linear-gradient(135deg, #57D76C, #27B782)',
  'linear-gradient(135deg, #FF8C42, #FF3C3C)',
];

const emit = defineEmits([
  'update:form',
  'avatarChanged',
  'coverChanged',
  'updated',
]);

const userId = localStorage.getItem('user_id');
const user = ref({});
const regionList = ref([]);

const isLoadingProfile = ref(true);
const isSaving = ref(false);
const isUploadingCover = ref(false);
const isAvatarLoading = ref(false);
const hasChanges = ref(false);
const initializing = ref(true);

const originalUser = ref({});
const coverLoaded = ref(false);
const coverBg = ref('');

const phoneInputRef = ref(null);
const avatarInputRef = ref(null);
const coverInputRef = ref(null);

const showError = ref(false);
const errorMessage = ref('');
const showSnackbar = ref(false);
const snackbarMessage = ref('');
const snackbarType = ref('success');

const showRegionDrawer = ref(false);
const selectedRegion = ref(null);
const showHashtagDrawer = ref(false);
const showCurrencyMenu = ref(false);
const currencies = ['₽', '$', '€'];

let phoneMask = null;
let snackbarTimer = null;

const form = ref({
  name: '',
  specifics: '',
  phone_number: '',
  email: '',
  about_self: '',
  is_phone_number_hidden: false,
  is_remote: false,
  salary: '',
  salary_currency: '₽',
});

const locationText = computed(() => {
  const city = user.value?.city?.name;
  const region = user.value?.city?.region_name;

  if (city && region) return `${city}, ${region}`;
  if (city) return city;
  if (region) return region;
  return 'не указан';
});

function showSnackbarMessage(message, type = 'success') {
  snackbarMessage.value = message;
  snackbarType.value = type;
  showSnackbar.value = true;

  clearTimeout(snackbarTimer);
  snackbarTimer = setTimeout(() => {
    showSnackbar.value = false;
  }, 3000);
}

function getLocalProfile() {
  try {
    return JSON.parse(localStorage.getItem('user_profile')) || {};
  } catch {
    return {};
  }
}

function updateUserCache(patch) {
  user.value = {
    ...user.value,
    ...patch,
  };

  localStorage.setItem('user_profile', JSON.stringify(user.value));
}

function pickRandomGradient() {
  return gradients[Math.floor(Math.random() * gradients.length)];
}

function setCover(url = user.value?.cover_url) {
  if (!url) {
    coverBg.value = pickRandomGradient();
    coverLoaded.value = true;
    return;
  }

  coverLoaded.value = false;

  const img = new Image();

  img.onload = () => {
    coverBg.value = `url(${url})`;
    coverLoaded.value = true;
  };

  img.onerror = () => {
    coverBg.value = pickRandomGradient();
    coverLoaded.value = true;
  };

  img.src = url;
}

function syncFormFromUser(profile) {
  initializing.value = true;

  Object.assign(form.value, {
    name: profile?.name || '',
    specifics: profile?.specifics || '',
    phone_number: profile?.phone_number || '',
    email: profile?.email || '',
    about_self: profile?.about_self || '',
    is_phone_number_hidden: !!profile?.is_phone_number_hidden,
    is_remote: !!profile?.is_remote,
    salary: profile?.salary || '',
    salary_currency: profile?.salary_currency || '₽',
  });

  originalUser.value = JSON.parse(JSON.stringify(form.value));
  selectedRegion.value = profile?.city || null;
  setCover(profile?.cover_url);

  nextTick(() => {
    initializing.value = false;
  });
}

function getComparableForm(value) {
  return {
    name: value.name || '',
    specifics: value.specifics || '',
    phone_number: value.phone_number || '',
    email: value.email || '',
    about_self: value.about_self || '',
    salary: value.salary || '',
    salary_currency: value.salary_currency || '₽',
    is_remote: !!value.is_remote,
    is_phone_number_hidden: !!value.is_phone_number_hidden,
  };
}

async function loadProfile() {
  const startedAt = performance.now();
  isLoadingProfile.value = true;

  const cached = getLocalProfile();

  if (cached && Object.keys(cached).length) {
    user.value = cached;
    syncFormFromUser(cached);
  }

  try {
    const fetched = await UserService.getProfile(userId);
    user.value = fetched || {};
    localStorage.setItem('user_profile', JSON.stringify(user.value));
    syncFormFromUser(user.value);
  } catch (error) {
    console.error('❌ Ошибка при загрузке профиля:', error);
    showSnackbarMessage('Не удалось загрузить профиль', 'error');

    if (!Object.keys(user.value || {}).length) {
      syncFormFromUser({});
    }
  } finally {
    const minSkeletonMs = 400;
    const elapsed = performance.now() - startedAt;
    const delay = Math.max(0, minSkeletonMs - elapsed);

    setTimeout(() => {
      isLoadingProfile.value = false;
    }, delay);
  }
}

function initPhoneMask() {
  if (!phoneInputRef.value || phoneMask) return;

  phoneMask = IMask(phoneInputRef.value, {
    mask: [
      {
        mask: '+{7} (000) 000-00-00',
        startsWith: '7',
        lazy: false,
      },
      {
        mask: '+{1} (000) 000-0000',
        startsWith: '1',
        lazy: false,
      },
      {
        mask: '+000000000000',
        lazy: false,
      },
    ],
    dispatch(appended, dynamicMasked) {
      const number = (dynamicMasked.value + appended).replace(/\D/g, '');
      return (
        dynamicMasked.compiledMasks.find((m) => number.startsWith(m.startsWith)) ||
        dynamicMasked.compiledMasks[2]
      );
    },
  });
}

function openRegionDrawer() {
  showRegionDrawer.value = true;
}

function toggleCurrencyMenu() {
  showCurrencyMenu.value = !showCurrencyMenu.value;
}

function changeCurrency(currency) {
  form.value.salary_currency = currency;
  showCurrencyMenu.value = false;
}

function closeCurrencyMenu() {
  showCurrencyMenu.value = false;
}

async function onSave() {
  if (isSaving.value) return;

  isSaving.value = true;

  try {
    const payload = {
      name: (form.value.name || '').trim() || null,
      specifics: (form.value.specifics || '').trim() || null,
      phone_number:
        (form.value.phone_number || '').replace(/\s|\(|\)|-/g, '') || null,
      email: (form.value.email || '').trim() || null,
      about_self: (form.value.about_self || '').trim() || null,
      is_phone_number_hidden: !!form.value.is_phone_number_hidden,
      is_remote: !!form.value.is_remote,
      salary: form.value.salary
        ? Number(String(form.value.salary).replace(/[^\d]/g, ''))
        : null,
      salary_currency: form.value.salary_currency || '₽',
    };

    await UserService.updateUserProfile(payload);

    updateUserCache(payload);
    originalUser.value = JSON.parse(JSON.stringify(form.value));
    hasChanges.value = false;

    emit('updated', payload);
    showSnackbarMessage('Профиль сохранён!');
  } catch (err) {
    console.error('❌ Ошибка при сохранении', err);
    errorMessage.value = 'Произошла ошибка при сохранении 😥';
    showError.value = true;
    showSnackbarMessage('Произошла ошибка при сохранении 😥', 'error');
  } finally {
    isSaving.value = false;
  }
}

async function onAvatarChange(e) {
  const file = e.target.files?.[0];
  if (!file) return;

  isAvatarLoading.value = true;

  try {
    const result = await UserService.uploadUserAvatar(file, true);

    if (result?.url) {
      const freshUrl = `${result.url}?v=${Date.now()}`;
      updateUserCache({ image_url: freshUrl });
      emit('avatarChanged', freshUrl);
    }

    showSnackbarMessage('Аватар успешно обновлён ✅');
  } catch (error) {
    console.error('❌ Ошибка при загрузке аватара:', error);
    errorMessage.value = 'Ошибка при загрузке аватара 😢';
    showError.value = true;
    showSnackbarMessage('Ошибка при загрузке аватара 😢', 'error');
  } finally {
    isAvatarLoading.value = false;
    e.target.value = '';
  }
}

async function onCoverChange(e) {
  const file = e.target.files?.[0];
  if (!file) return;

  isUploadingCover.value = true;

  try {
    const result = await UserService.uploadUserCover(file, true);

    if (result?.url) {
      const freshUrl = `${result.url}?v=${Date.now()}`;
      coverBg.value = `url(${freshUrl})`;
      coverLoaded.value = true;
      updateUserCache({ cover_url: freshUrl });
      emit('coverChanged', freshUrl);
      emit('updated', { cover_url: freshUrl });
    }

    showSnackbarMessage('Обложка успешно обновлена');
  } catch (error) {
    console.error('❌ Ошибка при загрузке обложки:', error);
    errorMessage.value = 'Ошибка при загрузке обложки 😢';
    showError.value = true;
    showSnackbarMessage('Ошибка при загрузке обложки 😢', 'error');
  } finally {
    isUploadingCover.value = false;
    e.target.value = '';
  }
}

onMounted(async () => {
  window.addEventListener('click', closeCurrencyMenu);
  await loadProfile();
  await nextTick();
  initPhoneMask();
});

onBeforeUnmount(() => {
  window.removeEventListener('click', closeCurrencyMenu);
  phoneMask?.destroy();
  clearTimeout(snackbarTimer);
});

watch(
  form,
  (newVal) => {
    if (initializing.value) return;

    hasChanges.value =
      JSON.stringify(getComparableForm(newVal)) !==
      JSON.stringify(getComparableForm(originalUser.value));

    emit('update:form', newVal);
  },
  { deep: true }
);

watch(isLoadingProfile, async (loading) => {
  if (!loading) {
    await nextTick();
    initPhoneMask();
  }
});
</script>

<style scoped>
.m-profile-page {
  width: 100vw;
  height: 100vh;
  height: 100dvh;
  background: var(--background-color);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.sticky-header {
  flex-shrink: 0;
}

.m-profile-content {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 16px;
  padding-bottom: 96px;
  box-sizing: border-box;
  -webkit-overflow-scrolling: touch;
}

.m-profile-general {
  width: 100%;
  max-width: 430px;
  margin: 0 auto;
  padding-bottom: 36px;
  border-radius: 24px;
  overflow: hidden;
  border: 1px solid #dedede;
  font-family: 'Eastman-Regular', sans-serif;
  position: relative;
}

[data-theme='light'] .m-profile-general {
  background: #ffffff;
}

[data-theme='dark'] .m-profile-general {
  background: #2a2a2a;
  border-color: #444;
}

.cover {
  width: 100%;
  height: 148px;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  position: relative;
  overflow: hidden;
}

.cover-skeleton {
  position: absolute;
  inset: 0;
  background:
    linear-gradient(
      90deg,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0.35) 50%,
      rgba(255, 255, 255, 0) 100%
    ),
    var(--cover-skeleton-base, #e9e9e9);
  background-size: 200% 100%;
  animation: cover-shimmer 1.1s linear infinite;
  pointer-events: none;
  z-index: 2;
}

[data-theme='dark'] .cover-skeleton {
  --cover-skeleton-base: #3a3a3a;
}

.edit-cover-btn {
  position: absolute;
  right: 16px;
  bottom: 16px;
  width: 42px;
  height: 42px;
  border: none;
  border-radius: 50%;
  background: rgba(33, 115, 229, 0.95);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.18);
  z-index: 5;
}

.edit-cover-btn:disabled {
  opacity: 0.75;
  cursor: not-allowed;
}

.edit-icon {
  width: 18px;
  height: 18px;
}

.avatar-wrapper {
  position: relative;
  width: 104px;
  height: 104px;
  margin-top: -52px;
  margin-left: 20px;
  z-index: 8;
}

.avatar {
  width: 104px;
  height: 104px;
  object-fit: cover;
  border-radius: 50%;
  border: 4px solid #fff;
  background: #eee;
  cursor: pointer;
  transition: 0.25s ease;
}

[data-theme='dark'] .avatar {
  border-color: #2a2a2a;
}

.avatar-wrapper:hover .avatar {
  transform: scale(1.03);
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.15);
}

.content {
  padding: 0 20px;
}

.name-block {
  margin-top: 14px;
}

.user-name {
  font-family: 'Eastman-Bold', sans-serif;
  font-size: 22px;
  line-height: 1.15;
  letter-spacing: -0.6px;
  color: #222;
}

[data-theme='dark'] .user-name {
  color: #fff;
}

.company-location {
  margin-top: 8px;
  font-size: 12px;
  line-height: 1.5;
  font-family: 'Eastman-Medium', sans-serif;
  color: #3a3a3a;
  cursor: pointer;
}

[data-theme='dark'] .company-location {
  color: #e0e0e0;
}

.location-tag {
  display: inline-flex;
  margin-left: 4px;
  margin-top: 4px;
  background: linear-gradient(to right, #7b61ff, #c972e6);
  color: white;
  border-radius: 999px;
  padding: 7px 11px;
  font-size: 10px;
  font-family: 'Eastman-Medium', sans-serif;
}

.input-section {
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-top: 26px;
  margin-bottom: 20px;
}

.input-section label,
.salary-label {
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 12px;
  color: #444;
  font-weight: 500;
}

[data-theme='dark'] .input-section label,
[data-theme='dark'] .salary-label {
  color: #f0f0f0;
}

.input-section input,
.input-section textarea {
  width: 100%;
  box-sizing: border-box;
  padding: 13px 14px;
  border: 1px solid #d6d6d6;
  border-radius: 12px;
  font-size: 14px;
  font-family: inherit;
  background-color: #f6f6f6;
  color: #111;
  outline: none;
}

.input-section textarea {
  min-height: 150px;
  resize: vertical;
}

.input-section input::placeholder,
.input-section textarea::placeholder {
  color: #9a9a9a;
}

[data-theme='dark'] .input-section input,
[data-theme='dark'] .input-section textarea {
  background-color: #444;
  color: #fff;
  border-color: #444;
}

[data-theme='dark'] .input-section input::placeholder,
[data-theme='dark'] .input-section textarea::placeholder {
  color: #888;
}

.salary-wrapper {
  width: 100%;
  box-sizing: border-box;
  position: relative;
  display: flex;
  align-items: center;
  background-color: #f9f9f9;
  border: 1px solid #ddd;
  border-radius: 12px;
  padding: 0 14px;
  margin-top: 6px;
  margin-bottom: 24px;
}

[data-theme='dark'] .salary-wrapper {
  background-color: #2a2a2a;
  border-color: #444;
}

.salary-input {
  min-width: 0;
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  font-size: 14px;
  font-family: inherit;
  color: #222;
  padding: 14px 0;
}

.salary-input::placeholder {
  color: #b0b0b0;
}

[data-theme='dark'] .salary-input,
[data-theme='dark'] .salary-currency {
  color: #fff;
}

[data-theme='dark'] .salary-input::placeholder {
  color: #aaa;
}

.currency-wrapper {
  position: relative;
  margin-left: 10px;
  font-size: 16px;
  font-family: 'Eastman-Bold', sans-serif;
  color: #444;
  cursor: pointer;
  user-select: none;
}

.currency-dropdown {
  position: absolute;
  top: 30px;
  right: 0;
  background: #3b3b3b;
  border-radius: 8px;
  list-style: none;
  padding: 5px 0;
  min-width: 52px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2);
  z-index: 20;
}

.currency-dropdown li {
  padding: 9px 15px;
  font-size: 14px;
  font-family: 'Eastman-Medium', sans-serif;
  color: white;
  cursor: pointer;
}

.currency-dropdown li:hover {
  background: #505050;
}

.switch-section {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.switch-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  font-size: 14px;
  font-family: 'Eastman-Medium', sans-serif;
  color: #333;
}

[data-theme='dark'] .switch-item {
  color: #fff;
}

.switch-item input[type='checkbox'] {
  display: none;
}

.slider {
  flex: 0 0 auto;
  position: relative;
  width: 42px;
  height: 24px;
  background-color: #ccc;
  border-radius: 999px;
  transition: background 0.3s;
  cursor: pointer;
}

.slider::before {
  content: '';
  position: absolute;
  top: 3px;
  left: 3px;
  width: 18px;
  height: 18px;
  background-color: white;
  border-radius: 50%;
  transition: transform 0.3s;
}

.switch-item input[type='checkbox']:checked + .slider {
  background-color: #2173e5;
}

.switch-item input[type='checkbox']:checked + .slider::before {
  transform: translateX(18px);
}

.floating-save-btn {
  position: fixed;
  left: 20px;
  right: 20px;
  bottom: calc(76px + env(safe-area-inset-bottom));
  height: 50px;
  background-color: #2173e5;
  color: white;
  border: none;
  border-radius: 999px;
  font-size: 15px;
  font-family: 'Eastman-Medium', sans-serif;
  cursor: pointer;
  box-shadow: 0 8px 24px rgba(33, 115, 229, 0.28);
  z-index: 100;
}

.floating-save-btn:disabled {
  cursor: not-allowed;
  opacity: 0.9;
}

.floating-save-btn.is-saving {
  background: linear-gradient(
    90deg,
    rgba(33, 115, 229, 1) 20%,
    rgba(90, 160, 255, 1) 50%,
    rgba(33, 115, 229, 1) 80%
  );
  background-size: 200% 100%;
  animation: btn-shimmer 1.2s linear infinite;
}

.snackbar {
  position: fixed;
  left: 50%;
  bottom: calc(138px + env(safe-area-inset-bottom));
  transform: translateX(-50%);
  max-width: calc(100vw - 32px);
  background: #2173e5;
  color: white;
  padding: 10px 18px;
  border-radius: 14px;
  font-family: 'Eastman-Medium', sans-serif;
  font-size: 13px;
  box-shadow: 0 4px 18px rgba(0, 0, 0, 0.22);
  z-index: 110;
}

.loader-overlay {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 20;
}

.loader-spinner,
.cover-loader-spinner {
  border-style: solid;
  border-top-color: transparent;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.loader-spinner {
  width: 30px;
  height: 30px;
  border-width: 4px;
  border-color: #2173e5;
  border-top-color: transparent;
}

.cover-loader-overlay {
  position: absolute;
  right: 20px;
  bottom: 20px;
  width: 42px;
  height: 42px;
  background: rgba(255, 255, 255, 0.45);
  backdrop-filter: blur(3px);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
}

.cover-loader-spinner {
  width: 18px;
  height: 18px;
  border-width: 3px;
  border-color: rgba(33, 115, 229, 0.65);
  border-top-color: transparent;
}

.profile-skeleton {
  width: 100%;
  max-width: 430px;
  margin: 0 auto;
  padding: 16px;
  box-sizing: border-box;
  border-radius: 24px;
  background: var(--sidebar-background);
}

.skeleton {
  background: linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%);
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.2s infinite;
  border-radius: 14px;
}

[data-theme='dark'] .skeleton {
  background: linear-gradient(90deg, #333 25%, #444 50%, #333 75%);
  background-size: 200% 100%;
}

.cover-skeleton-block {
  height: 148px;
  border-radius: 18px;
}

.avatar-skeleton {
  width: 104px;
  height: 104px;
  border-radius: 50%;
  margin-top: -34px;
  margin-left: 8px;
}

.name-skeleton {
  width: 60%;
  height: 24px;
  margin-top: 18px;
}

.field-skeleton {
  width: 100%;
  height: 48px;
  margin-top: 16px;
}

.field-skeleton.large {
  height: 150px;
}

.slide-fade-enter-active,
.slide-fade-leave-active,
.fade-enter-active,
.fade-leave-active {
  transition: all 0.25s ease;
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(8px);
}

@keyframes cover-shimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}

@keyframes skeleton-shimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}

@keyframes btn-shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@media (min-width: 768px) {
  .m-profile-page {
    max-width: 430px;
    margin: 0 auto;
    border-left: 1px solid var(--separator_home);
    border-right: 1px solid var(--separator_home);
  }
}

.back-btn {
position: absolute;
top: 14px;
left: 14px;
height: 36px;
padding: 0 14px;
border: none;
border-radius: 999px;
background: rgba(255,255,255,0.92);
backdrop-filter: blur(8px);
color: #111;
font-size: 13px;
font-family: 'Eastman-Medium', sans-serif;
display: flex;
align-items: center;
justify-content: center;
gap: 6px;
z-index: 50;
box-shadow: 0 4px 14px rgba(0,0,0,0.12);
}
[data-theme='dark'] .back-btn {
background: rgba(40,40,40,0.92);
color: #fff;
}

@media (max-width: 360px) {
  .m-profile-content {
    padding-left: 12px;
    padding-right: 12px;
  }

  .content {
    padding: 0 16px;
  }

  .avatar-wrapper {
    margin-left: 16px;
  }

  .user-name {
    font-size: 20px;
  }
}
</style>
